import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import { ShoppingBag, Plus, Save, Trash2, Edit2, Link as LinkIcon, CheckCircle, XCircle, Loader2, Wifi, WifiOff, Package, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

interface WooStore {
  id: string;
  store_name: string;
  store_url: string;
  consumer_key: string;
  consumer_secret: string;
  is_active: boolean;
}

interface TestResult {
  storeId: string;
  status: 'idle' | 'testing' | 'success' | 'error';
  connected: boolean;
  productsCount?: number;
  storeName?: string;
  wooVersion?: string;
  errorMessage?: string;
  responseTime?: number;
}

export default function WooCommerceSettings() {
  const { user } = useAuth();
  const [stores, setStores] = useState<WooStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingStore, setEditingStore] = useState<Partial<WooStore> | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  useEffect(() => {
    if (user) fetchStores();
  }, [user]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('woocommerce_stores')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setStores(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Błąd pobierania sklepów');
    } finally {
      setLoading(false);
    }
  };

  const saveStore = async () => {
    if (!editingStore?.store_name || !editingStore?.store_url || !editingStore?.consumer_key || !editingStore?.consumer_secret) {
      return toast.error('Wypełnij wszystkie pola');
    }
    
    try {
      const payload = {
        user_id: user?.id,
        store_name: editingStore.store_name,
        store_url: editingStore.store_url,
        consumer_key: editingStore.consumer_key,
        consumer_secret: editingStore.consumer_secret,
        is_active: editingStore.is_active ?? true,
      };

      if (editingStore.id) {
        const { error } = await supabase.from('woocommerce_stores').update(payload).eq('id', editingStore.id);
        if (error) throw error;
        toast.success('Zaktualizowano sklep');
      } else {
        const { error } = await supabase.from('woocommerce_stores').insert(payload);
        if (error) throw error;
        toast.success('Dodano nowy sklep');
      }
      
      setEditingStore(null);
      fetchStores();
    } catch (err) {
      console.error(err);
      toast.error('Błąd zapisywania');
    }
  };

  const deleteStore = async (id: string) => {
    if (!confirm('Na pewno usunąć ten sklep?')) return;
    try {
      const { error } = await supabase.from('woocommerce_stores').delete().eq('id', id);
      if (error) throw error;
      toast.success('Usunięto sklep');
      setTestResults(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      fetchStores();
    } catch (err) {
      console.error(err);
      toast.error('Błąd usuwania');
    }
  };

  const toggleStatus = async (store: WooStore) => {
    try {
      const { error } = await supabase.from('woocommerce_stores').update({ is_active: !store.is_active }).eq('id', store.id);
      if (error) throw error;
      fetchStores();
    } catch (err) {
      console.error(err);
    }
  };

  const testConnection = async (store: WooStore) => {
    setTestResults(prev => ({
      ...prev,
      [store.id]: { storeId: store.id, status: 'testing', connected: false }
    }));

    const startTime = performance.now();

    try {
      // Normalize URL
      let baseUrl = store.store_url.replace(/\/+$/, '');
      
      // Build WooCommerce REST API URL
      const authParams = `consumer_key=${encodeURIComponent(store.consumer_key)}&consumer_secret=${encodeURIComponent(store.consumer_secret)}`;
      
      // Test 1: Check system status
      const systemUrl = `${baseUrl}/wp-json/wc/v3/system_status?${authParams}`;
      let wooVersion = '';
      let storeName = store.store_name;

      try {
        const sysRes = await fetch(systemUrl, { 
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        });
        if (sysRes.ok) {
          const sysData = await sysRes.json();
          wooVersion = sysData?.environment?.version || '';
          storeName = sysData?.settings?.store_name || sysData?.environment?.site_url || store.store_name;
        }
      } catch {
        // system_status might not be accessible, that's OK
      }

      // Test 2: Fetch products count (main connectivity test)
      const productsUrl = `${baseUrl}/wp-json/wc/v3/products?${authParams}&per_page=1&page=1`;
      const prodRes = await fetch(productsUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      const responseTime = Math.round(performance.now() - startTime);

      if (!prodRes.ok) {
        const errorBody = await prodRes.text();
        let errorMessage = `HTTP ${prodRes.status}`;
        
        try {
          const parsed = JSON.parse(errorBody);
          if (parsed.message) errorMessage = parsed.message;
          else if (parsed.data?.status) errorMessage = `Status ${parsed.data.status}: ${parsed.code || 'Nieznany błąd'}`;
        } catch {
          if (prodRes.status === 401) errorMessage = 'Nieprawidłowy Consumer Key lub Consumer Secret';
          else if (prodRes.status === 403) errorMessage = 'Brak uprawnień – sprawdź uprawnienia kluczy API w WooCommerce';
          else if (prodRes.status === 404) errorMessage = 'Nie znaleziono WooCommerce REST API – sprawdź czy wtyczka WooCommerce jest aktywna';
          else errorMessage = `Serwer zwrócił błąd ${prodRes.status}`;
        }
        
        throw new Error(errorMessage);
      }

      // Extract total products from headers
      const totalProducts = parseInt(prodRes.headers.get('X-WP-Total') || '0', 10);
      
      // If no wooVersion, try to get it from response headers
      if (!wooVersion) {
        // Check if the response looks valid
        const prodData = await prodRes.json();
        if (Array.isArray(prodData)) {
          wooVersion = 'REST API v3';
        }
      }

      setTestResults(prev => ({
        ...prev,
        [store.id]: {
          storeId: store.id,
          status: 'success',
          connected: true,
          productsCount: totalProducts,
          storeName,
          wooVersion: wooVersion || 'REST API v3',
          responseTime,
        }
      }));

      toast.success(`✅ Połączenie z "${store.store_name}" działa prawidłowo!`);
    } catch (err: any) {
      const responseTime = Math.round(performance.now() - startTime);
      
      let errorMessage = err.message || 'Nieznany błąd';
      
      // Handle network / CORS errors
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        errorMessage = 'Nie można połączyć się ze sklepem. Sprawdź czy: URL jest poprawny, sklep jest online, certyfikat SSL jest prawidłowy, CORS jest skonfigurowany.';
      }

      setTestResults(prev => ({
        ...prev,
        [store.id]: {
          storeId: store.id,
          status: 'error',
          connected: false,
          errorMessage,
          responseTime,
        }
      }));

      toast.error(`❌ Połączenie z "${store.store_name}" nie powiodło się`);
    }
  };

  const getTestResult = (storeId: string): TestResult | null => {
    return testResults[storeId] || null;
  };

  return (
    <div className="bg-slate-900/90 rounded-xl p-6 border border-slate-700/50 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white flex items-center">
          <ShoppingBag className="w-5 h-5 inline mr-2 text-indigo-500" />
          Zarządzanie Sklepami WooCommerce
        </h2>
        <button 
          onClick={() => setEditingStore({})}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors text-sm font-bold"
        >
          <Plus className="w-4 h-4" /> Dodaj Sklep
        </button>
      </div>

      <div className="mb-6 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
        <p className="text-sm text-indigo-300 mb-2">
          Możesz podłączyć wiele sklepów WooCommerce jednocześnie. Produkty ze wszystkich aktywnych sklepów będą dostępne w "Marketing Studio PRO" w zakładce "Z Produktów Woo".
        </p>
      </div>

      {editingStore !== null && (
        <div className="bg-[#1e293b] p-6 rounded-xl border border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.1)] mb-6 space-y-4 relative">
          <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <Edit2 className="w-5 h-5 text-indigo-400" />
            {editingStore.id ? 'Edycja sklepu WooCommerce' : 'Dodawanie nowego sklepu WooCommerce'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Nazwa Sklepu (widoczna w panelu)</label>
              <input type="text" placeholder="np. Sklep odzieżowy PL" value={editingStore.store_name || ''} onChange={e => setEditingStore({...editingStore, store_name: e.target.value})} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Adres URL Sklepu</label>
              <input type="url" placeholder="https://twojsklep.pl" value={editingStore.store_url || ''} onChange={e => setEditingStore({...editingStore, store_url: e.target.value})} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono" />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Consumer Key (Klucz klienta)</label>
              <input type="text" placeholder="ck_..." value={editingStore.consumer_key || ''} onChange={e => setEditingStore({...editingStore, consumer_key: e.target.value})} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono" />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-300 mb-2">Consumer Secret (Sekret klienta)</label>
              <input type="password" placeholder="cs_..." value={editingStore.consumer_secret || ''} onChange={e => setEditingStore({...editingStore, consumer_secret: e.target.value})} className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700 mt-4">
            <button onClick={() => setEditingStore(null)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-bold">Anuluj</button>
            <button onClick={saveStore} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold flex items-center gap-2"><Save className="w-4 h-4" /> Zapisz</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-slate-700 border-t-indigo-500 rounded-full animate-spin"/></div>
      ) : stores.length === 0 ? (
        <div className="text-center p-8 bg-slate-800/30 rounded-xl border border-slate-700/50">
          <ShoppingBag className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 font-medium">Brak dodanych sklepów WooCommerce</p>
        </div>
      ) : (
        <div className="space-y-4">
          {stores.map(store => {
            const result = getTestResult(store.id);
            
            return (
              <div key={store.id} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden transition-all">
                {/* Store Header Row */}
                <div className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`p-3 rounded-lg ${store.is_active ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-700/50 text-slate-500'}`}>
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg flex items-center gap-2">
                        {store.store_name}
                        {!store.is_active && <span className="text-[10px] px-2 py-0.5 bg-slate-700 text-slate-300 rounded uppercase tracking-wider">Nieaktywny</span>}
                      </h3>
                      <p className="text-slate-400 text-sm font-mono flex items-center gap-1.5 mt-0.5">
                        <LinkIcon className="w-3.5 h-3.5" /> {store.store_url}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full md:w-auto">
                    {/* Test Connection Button */}
                    <button
                      onClick={() => testConnection(store)}
                      disabled={result?.status === 'testing'}
                      className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all border ${
                        result?.status === 'testing'
                          ? 'bg-blue-500/10 border-blue-500/30 text-blue-300 cursor-wait'
                          : result?.status === 'success'
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
                          : result?.status === 'error'
                          ? 'bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20'
                          : 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20'
                      }`}
                    >
                      {result?.status === 'testing' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : result?.status === 'success' ? (
                        <Wifi className="w-4 h-4" />
                      ) : result?.status === 'error' ? (
                        <WifiOff className="w-4 h-4" />
                      ) : (
                        <Wifi className="w-4 h-4" />
                      )}
                      {result?.status === 'testing'
                        ? 'Testowanie...'
                        : result?.status === 'success'
                        ? 'Połączono ✓'
                        : result?.status === 'error'
                        ? 'Ponów test'
                        : 'Testuj połączenie'}
                    </button>

                    <button onClick={() => toggleStatus(store)} className={`flex-1 md:flex-none px-3 py-2 rounded-lg font-bold text-sm transition-colors border ${store.is_active ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'}`}>
                      {store.is_active ? 'Wyłącz' : 'Włącz'}
                    </button>
                    <button onClick={() => {
                      setEditingStore(store);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }} className="px-3 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deleteStore(store.id)} className="px-3 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-lg transition-colors border border-rose-500/30"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {/* Test Results Panel (slides in) */}
                {result && result.status !== 'idle' && (
                  <div className={`border-t px-5 py-4 transition-all animate-in fade-in slide-in-from-top-2 duration-300 ${
                    result.status === 'success' 
                      ? 'border-emerald-500/30 bg-emerald-500/5' 
                      : result.status === 'error' 
                      ? 'border-rose-500/30 bg-rose-500/5' 
                      : 'border-blue-500/30 bg-blue-500/5'
                  }`}>
                    {result.status === 'testing' && (
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-400 rounded-full animate-spin" />
                        <span className="text-sm text-blue-300 font-medium">Sprawdzanie połączenia z WooCommerce REST API...</span>
                      </div>
                    )}

                    {result.status === 'success' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                          <span className="text-sm font-bold text-emerald-300">Połączenie nawiązane pomyślnie!</span>
                          {result.responseTime && (
                            <span className="text-[11px] px-2 py-0.5 bg-emerald-500/15 text-emerald-400 rounded-full border border-emerald-500/20 ml-auto">
                              {result.responseTime}ms
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="flex items-center gap-2.5 p-3 bg-slate-800/60 rounded-lg border border-slate-700/50">
                            <Package className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                            <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-wide font-bold">Produkty</p>
                              <p className="text-white font-black text-lg leading-tight">{result.productsCount ?? '—'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 p-3 bg-slate-800/60 rounded-lg border border-slate-700/50">
                            <ShoppingBag className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                            <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-wide font-bold">Wersja WooCommerce</p>
                              <p className="text-white font-bold text-sm leading-tight">{result.wooVersion || '—'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 p-3 bg-slate-800/60 rounded-lg border border-slate-700/50">
                            <Wifi className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                            <div>
                              <p className="text-[11px] text-slate-500 uppercase tracking-wide font-bold">Status</p>
                              <p className="text-emerald-400 font-bold text-sm leading-tight">Online</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {result.status === 'error' && (
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <XCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-rose-300">Połączenie nie powiodło się</p>
                            <p className="text-xs text-rose-200/80 mt-1">{result.errorMessage}</p>
                          </div>
                          {result.responseTime && (
                            <span className="text-[11px] px-2 py-0.5 bg-rose-500/15 text-rose-400 rounded-full border border-rose-500/20 ml-auto flex-shrink-0">
                              {result.responseTime}ms
                            </span>
                          )}
                        </div>
                        <div className="mt-2 p-3 bg-slate-800/40 rounded-lg border border-slate-700/50">
                          <p className="text-xs text-slate-400 flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <span>
                              <strong className="text-slate-300">Wskazówki:</strong> Upewnij się, że adres URL jest poprawny (z https://), 
                              klucze API mają uprawnienia do odczytu, wtyczka WooCommerce jest aktywna, 
                              oraz serwer sklepu jest dostępny.
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
